import { useState, useCallback, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { Menu, Plus, Settings, Info, Upload, AtSign, Search } from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  type ChatAttachmentRef,
  type ChatChannelResponse,
  type ChatMessageSearchHitResponse,
  type ChatPeopleSearchHit,
} from '@/lib/api';
import { useFileDropZone } from '@/lib/hooks';
import { clipboardAttachmentFiles, searchHitChannelLabel } from '../utils';
import { useChat, useFilePasteCapture } from '../hooks';
import { useChatChannel } from '../hooks/useChatChannel';
import { useChatSettings } from '../hooks/useChatSettings';
import { useMessageReactions } from '../hooks/useMessageReactions';
import { useChannelMessagePermissions } from '../hooks/useChannelMessagePermissions';
// Guild names/icons for the chat list's collapsible guild groups. Shares the
// `guilds.my()` query cache with the guilds arche rather than refetching.
import { useMyGuilds } from '../../guilds/hooks/useMyGuilds';
import { useUnreadCutoff } from '../hooks/useUnreadCutoff';
import { FilterableChannelList } from '../components/FilterableChannelList';
import { ChannelHeader } from '../components/ChannelHeader';
import { MessageList, type MessageListHandle } from '../components/MessageList';
// MessageInput pulls in TipTap + ProseMirror (~150 KB). Code-split it out of
// the main bundle so users who never open the chat arche don't pay the cost.
const MessageInput = lazy(() =>
  import('../components/MessageInput').then((m) => ({ default: m.MessageInput })),
);
import type { MessageInputHandle } from '../components/MessageInput';
import { TypingIndicator } from '../components/TypingIndicator';
import { ChatSettingsModal } from '../components/ChatSettingsModal';
import { MentionsPanel } from '../components/MentionsPanel';
import { AllChatsSearchPanel } from '../components/AllChatsSearchPanel';
import { NewChatDialog } from '../components/NewChatDialog';
import { ChannelMessagePreview } from '../components/ChannelMessagePreview';
import { GuildDirectMessageDialog } from '../components/GuildDirectMessageDialog';
import { useAuth } from '@/auth';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { ArcheExpandButton } from '../../../components/ArcheExpandButton';
import { useSidebar } from '../../../context/SidebarContext';
import { archeInfoPath } from '@/lib/arche-url';

interface ChatPageProps {
  /** Channel ID to use when none is provided via URL params (e.g. /chat route) */
  defaultChannelId?: string;
}

export function ChatPage({ defaultChannelId }: ChatPageProps = {}) {
  const { channelId: urlChannelId } = useParams<{ channelId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.userId || null;
  const effectiveChannelId = urlChannelId || defaultChannelId || null;
  const [activeChannelId, setActiveChannelId] = useState<string | null>(effectiveChannelId);
  // Channel being peeked at via press-and-hold — a read-only preview that does
  // NOT mark the channel read (it never becomes the active channel).
  const [previewChannel, setPreviewChannel] = useState<ChatChannelResponse | null>(null);

  // Sync activeChannelId when the URL param or default changes (e.g. browser back/forward)
  useEffect(() => {
    setActiveChannelId(effectiveChannelId);
  }, [effectiveChannelId]);

  const { channels, messages, typing } = useChat(activeChannelId);
  const {
    channel: activeChannel,
    members,
    memberCount,
    refresh: refreshChannel,
  } = useChatChannel(activeChannelId);
  const { guilds: myGuilds } = useMyGuilds();

  // The WebSocket typing event only carries the sender's userId (the WS service
  // has no display-name cache), so resolve it to a human name from the channel
  // members here rather than ever showing a raw "usr_…" id in the indicator.
  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      const name = m.displayName || m.nickname;
      if (name) map.set(m.userId, name);
    }
    return map;
  }, [members]);

  // Stable refs to avoid re-creating callbacks when messages/channels change
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  const handleSelectChannel = useCallback(
    (channelId: string) => {
      setActiveChannelId(channelId);
      navigate(`/a/arche_chat/channel/${channelId}`, { replace: true });
    },
    [navigate],
  );

  const handleCreateDM = useCallback(
    async (targetUserId: string) => {
      const channel = await channelsRef.current.openDirectMessage(targetUserId);
      if (channel) handleSelectChannel(channel.channelId);
    },
    [handleSelectChannel],
  );

  // Opening a mention from the inbox switches to that conversation. (Anchoring the
  // scroll to the exact message across a channel switch is a future enhancement —
  // v1 lands the user in the right channel.)
  const handleSelectMention = useCallback(
    (item: { channelId: string }) => {
      handleSelectChannel(item.channelId);
    },
    [handleSelectChannel],
  );

  // A guild DM is a distinct channel from the personal DM with the same person,
  // so this deliberately passes the guild scope through rather than reusing
  // handleCreateDM.
  const [guildDmTarget, setGuildDmTarget] = useState<{ guildId: string; name: string } | null>(
    null,
  );

  const handleOpenGuildDm = useCallback(
    async (guildId: string, targetUserId: string) => {
      const channel = await channelsRef.current.openDirectMessage(targetUserId, guildId);
      if (channel) handleSelectChannel(channel.channelId);
    },
    [handleSelectChannel],
  );

  const handleCreateGroup = useCallback(
    async (name: string, description: string) => {
      const channel = await channelsRef.current.createChannel({
        channelType: 'group',
        name,
        description: description || undefined,
      });
      if (channel) handleSelectChannel(channel.channelId);
    },
    [handleSelectChannel],
  );

  // Edit state — lifted here so MessageInput can take over the edit flow
  const [editingMessage, setEditingMessage] = useState<{
    messageId: string;
    content: string;
  } | null>(null);

  // Reply state — lifted here so MessageInput shows the reply banner
  const [replyTo, setReplyTo] = useState<{
    messageId: string;
    senderName: string;
    content: string;
  } | null>(null);

  // Imperative handle to focus the composer. Focusing from within the reply tap
  // (a user gesture) is what reliably opens the mobile keyboard.
  const messageInputRef = useRef<MessageInputHandle>(null);

  // ----- Member-list actions (the header's roster popover) -----

  // Inserts an @mention for a member into this channel's composer — the same
  // mention node the typeahead writes, so the server derives a real mention
  // rather than the literal text "@name".
  const handleMentionMember = useCallback(
    (targetUserId: string) => {
      const member = members.find((m) => m.userId === targetUserId);
      // Never fall back to the raw userId: the mention node's `renderText`
      // serializes this label into the SENT message body, so an id here would
      // persist as "@usr_4b82…" for every reader. 'User' matches the terminal
      // fallback the server's own identity composer uses.
      const name = member?.nickname || member?.displayName || member?.username || 'User';
      messageInputRef.current?.insertMention(targetUserId, name);
    },
    [members],
  );

  // Removes a member from the current channel. Only offered to a viewer whose
  // own member row carries the permission; the server re-checks, so this gate
  // decides visibility, not authorization.
  const handleRemoveMember = useCallback(
    async (targetUserId: string) => {
      if (!activeChannelId) return;
      try {
        await api.removeChatMember(activeChannelId, targetUserId);
        refreshChannel();
      } catch (err) {
        // Say so. A removal the server refuses (the permission was revoked
        // between render and click, or the member already left) used to fail
        // silently, leaving the row in place — indistinguishable from a broken
        // button.
        toast({
          title: 'Could not remove that member',
          description: err instanceof Error ? err.message : 'Please try again.',
          variant: 'destructive',
        });
      }
    },
    [activeChannelId, refreshChannel],
  );

  // Mirrors ChannelSettingsModal's rule: an owner always may, an admin may
  // unless the permission was explicitly revoked on their member row.
  const canRemoveMembers = useMemo(() => {
    const me = members.find((m) => m.userId === currentUserId);
    if (!me) return false;
    if (me.role === 'owner') return true;
    return me.role === 'admin' && me.permissions?.banUsers !== false;
  }, [members, currentUserId]);

  // Drag-and-drop files anywhere over the conversation pane → hand them to the
  // composer, which filters by send permission/size and uploads them as pending
  // attachments. Disabled when there's no active channel or the user has left it.
  const handleFilesDropped = useCallback((files: File[]) => {
    messageInputRef.current?.addFiles(files);
  }, []);
  const { isDragActive: isFileDragActive, dropZoneProps } = useFileDropZone({
    onDrop: handleFilesDropped,
    disabled: !activeChannelId || !!activeChannel?.hasLeft,
  });

  // Paste a screen capture (or a copied file) anywhere in the chat window and it
  // becomes a pending attachment, as if it had been dropped. Pastes made with the
  // composer focused never reach this — the composer handles those itself and
  // marks the event handled — and pastes into a text field, or inside an open
  // dialog, belong there instead.
  //
  // Scoped to this page deliberately, mirroring the drop zone above:
  // EmbeddedChannelChat (guild pages) has neither, because a document-level
  // listener there would reach across a host page this arche does not own. Its
  // composer still handles pastes made into it.
  const handleFilesPasted = useCallback((clipboardData: DataTransfer) => {
    const files = clipboardAttachmentFiles(clipboardData);
    if (files.length === 0) return;
    messageInputRef.current?.addFiles(files);
    // Focus so the user can caption the attachment and press Enter without a
    // second click. Safe from a paste: it is already a user gesture.
    messageInputRef.current?.focus();
  }, []);
  useFilePasteCapture({
    onPaste: handleFilesPasted,
    disabled: !activeChannelId || !!activeChannel?.hasLeft,
  });

  const handleSendMessage = useCallback(
    async (
      content: string,
      replyToId?: string,
      attachmentRefs?: ChatAttachmentRef[],
    ): Promise<boolean> => {
      const sent = await messagesRef.current.sendMessage(content, replyToId, attachmentRefs);
      if (sent && activeChannelId) {
        channelsRef.current.bumpChannel(activeChannelId, {
          content: sent.content,
          senderId: sent.senderId,
          senderName: sent.senderName ?? null,
          createdAt: sent.createdAt,
        });
      }
      // Report success so the composer clears on send and preserves the user's text
      // (with a warning) when the send is rejected — no more silent drops.
      return sent !== null;
    },
    [activeChannelId],
  );

  const handleStartEdit = useCallback((messageId: string) => {
    const msg = messagesRef.current.messages.find((m) => m.messageId === messageId);
    if (msg) setEditingMessage({ messageId, content: msg.content });
  }, []);

  const handleSaveEdit = useCallback(async (messageId: string, content: string) => {
    await messagesRef.current.editMessage(messageId, content);
    setEditingMessage(null);
  }, []);

  const handleDeleteMessage = useCallback((messageId: string) => {
    messagesRef.current.deleteMessage(messageId);
  }, []);

  const handleReply = useCallback((messageId: string) => {
    const msg = messagesRef.current.messages.find((m) => m.messageId === messageId);
    if (!msg) return;
    setReplyTo({
      messageId,
      senderName: msg.senderName || 'Unknown',
      content: msg.content,
    });
    // Focus synchronously within the Reply tap so the mobile keyboard opens.
    messageInputRef.current?.focus();
  }, []);

  // Shared with the embedded guild-page pane so the optimistic-update logic
  // has exactly one implementation.
  const handleReaction = useMessageReactions(messagesRef, currentUserId);

  // Get current user ID and admin status. Chat is platform-wide (not a
  // litcraft-domain arche), so this gate is platform `admin` only —
  // `litcraft_admin` does NOT get cross-channel delete-anyone authority,
  // matching the server-side `requireAdmin` middleware on the chat
  // moderation routes. Channel-level role admin is a separate concept —
  // it's the per-channel `member.role`, resolved inside
  // useChannelMessagePermissions — and is unrelated to platform roles.
  const isAdmin = user?.roles?.includes('admin') ?? false;

  const currentChannelMember = members.find((m) => m.userId === currentUserId) ?? null;

  // Frozen "last read" marker for the unread-messages divider — captured before
  // opening the channel advances last_read_at via mark-as-read.
  const unreadCutoff = useUnreadCutoff(activeChannelId, currentChannelMember);

  // Effective channel-level composer permissions — shared with the embedded
  // guild-page pane so the resolution rules have exactly one implementation.
  const messagePermissions = useChannelMessagePermissions(activeChannel, currentChannelMember);
  const { isCollapsed, toggle } = useSidebar();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mentionsOpen, setMentionsOpen] = useState(false);

  // Search result → scroll the conversation to that message. Imperative rather
  // than a state signal: it's a one-shot action, not a piece of render state,
  // so picking the same result twice simply calls it twice.
  const messageListRef = useRef<MessageListHandle>(null);
  const handleJumpToMessage = useCallback((messageId: string) => {
    messageListRef.current?.jumpToMessage(messageId);
  }, []);

  // "Search all chats" → open the hit's conversation, then jump once its
  // messages have loaded. The jump waits until the loaded list actually belongs
  // to the target channel: right after the switch, the previous channel's
  // messages are still in state for a render. Navigating elsewhere first
  // abandons the jump rather than firing it later in the wrong place.
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  // The text the panel opens with — the sidebar filter hands its query over
  // when the loaded list has no match; the header button opens it empty.
  const [globalSearchInitialQuery, setGlobalSearchInitialQuery] = useState('');
  const openGlobalSearch = useCallback((query = '') => {
    setGlobalSearchInitialQuery(query);
    setGlobalSearchOpen(true);
  }, []);
  const [pendingJump, setPendingJump] = useState<{ channelId: string; messageId: string } | null>(
    null,
  );
  const handleSelectGlobalResult = useCallback(
    (hit: ChatMessageSearchHitResponse) => {
      setPendingJump({ channelId: hit.channelId, messageId: hit.messageId });
      handleSelectChannel(hit.channelId);
    },
    [handleSelectChannel],
  );
  // A person from the People section: open the DM that already exists, or
  // start one (the server returns the existing channel either way; this just
  // skips the round-trip when the panel already knows the answer).
  const handleSelectPerson = useCallback(
    (hit: ChatPeopleSearchHit) => {
      if (hit.existingChannelId) handleSelectChannel(hit.existingChannelId);
      else void handleCreateDM(hit.userId);
    },
    [handleSelectChannel, handleCreateDM],
  );
  useEffect(() => {
    if (!pendingJump) return;
    if (activeChannelId !== pendingJump.channelId) {
      setPendingJump(null);
      return;
    }
    if (messages.loading || messages.messages[0]?.channelId !== pendingJump.channelId) return;
    handleJumpToMessage(pendingJump.messageId);
    setPendingJump(null);
  }, [pendingJump, activeChannelId, messages.loading, messages.messages, handleJumpToMessage]);

  const channelById = useMemo(
    () => new Map(channels.channels.map((c) => [c.channelId, c])),
    [channels.channels],
  );
  const resolveSearchHitLabel = useCallback(
    (hit: ChatMessageSearchHitResponse) =>
      searchHitChannelLabel(hit, channelById.get(hit.channelId)),
    [channelById],
  );

  // Background from user settings
  const { settings: chatSettings } = useChatSettings();
  const [bgImageUrl, setBgImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!chatSettings.backgroundImageId) {
      setBgImageUrl(null);
      return;
    }
    let cancelled = false;
    api
      .getDownloadUrl(chatSettings.backgroundImageId)
      .then((r) => {
        if (!cancelled) setBgImageUrl(r.downloadUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chatSettings.backgroundImageId]);

  const msgAreaBgStyle: React.CSSProperties = bgImageUrl
    ? {
        backgroundImage: `url(${bgImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : chatSettings.backgroundColor
      ? { backgroundColor: chatSettings.backgroundColor }
      : {};

  // On mobile, show either the sidebar or the message pane — not both.
  const showMessagePane = !!activeChannelId;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Channel Sidebar
          Mobile: full-screen when no channel is selected, hidden when one is.
          Desktop: fixed 420px, always visible. */}
      <div
        className={[
          'relative flex-col flex-shrink-0 border-r border-border bg-card]',
          'md:flex md:w-[420px] md:min-w-[420px]',
          showMessagePane ? 'hidden' : 'flex w-full',
        ].join(' ')}
      >
        <div className="border-b border-border px-4 flex items-center justify-between !h-[55px] min-h-[55px]">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              className={`h-9 w-9 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
              title="Open sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold text-foreground">Chat</h1>
            <Link
              to={archeInfoPath('arche_chat')}
              className="text-muted-foreground hover:text-primary transition-colors"
              title="View arche details"
            >
              <Info className="h-5 w-5" />
            </Link>
            <FavoriteButton archeId="arche_chat" />
            <ArcheExpandButton archeId="arche_chat" />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openGlobalSearch()}
              title="Search all chats"
              aria-label="Search all chats"
              className="h-8 w-8"
            >
              <Search className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMentionsOpen(true)}
              title="Mentions"
              className="h-8 w-8"
            >
              <AtSign className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSettingsOpen(true)}
              title="Chat settings"
              className="h-8 w-8"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <FilterableChannelList
          channels={channels.channels}
          activeChannelId={activeChannelId}
          onSelectChannel={handleSelectChannel}
          onPreviewChannel={setPreviewChannel}
          loading={channels.loading}
          viewMode={chatSettings.appearanceChannelListView}
          guilds={myGuilds}
          onNewGuildDirectMessage={(guildId, name) => setGuildDmTarget({ guildId, name })}
          onSearchAll={openGlobalSearch}
        />

        {/* Floating action button — new DM / group */}
        <div className="absolute bottom-4 right-3">
          <NewChatDialog
            onCreateDM={handleCreateDM}
            onCreateGroup={handleCreateGroup}
            onCreateSelfDm={currentUserId ? () => handleCreateDM(currentUserId) : undefined}
            trigger={
              <div className="h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all">
                <Plus className="h-5 w-5" />
              </div>
            }
          />
        </div>
      </div>

      {/* Message Area
          Mobile: full-screen when a channel is selected, hidden otherwise.
          Desktop: fills remaining space, always visible. */}
      <div
        className={[
          'flex-1 flex-col min-w-0 overflow-hidden',
          showMessagePane ? 'flex' : 'hidden md:flex',
        ].join(' ')}
      >
        <ChannelHeader
          channel={activeChannel}
          memberCount={memberCount}
          currentUserId={currentUserId}
          members={members}
          onJumpToMessage={handleJumpToMessage}
          onOpenDm={handleCreateDM}
          onMentionMember={handleMentionMember}
          onRemoveMember={canRemoveMembers ? handleRemoveMember : undefined}
          onBack={() => setActiveChannelId(null)}
          onChannelLeft={() => {
            if (activeChannel?.channelType === 'direct') {
              setActiveChannelId(null);
              navigate('/a/arche_chat', { replace: true });
            }
            // Groups/guilds stay selected in read-only mode so the user can view history or rejoin
          }}
          onChannelDeleted={() => {
            setActiveChannelId(null);
            navigate('/a/arche_chat', { replace: true });
          }}
        />
        {/* Conversation drop zone — messages + composer, excluding the header.
            The overlay covers this region so a drop lands here rather than on the
            TipTap editor beneath. */}
        <div className="relative flex flex-1 flex-col min-h-0 overflow-hidden" {...dropZoneProps}>
          {isFileDragActive && (
            <div
              role="status"
              aria-live="polite"
              className="absolute inset-0 z-30 flex items-center justify-center bg-background/80 backdrop-blur-sm"
            >
              <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-primary bg-primary/5 px-10 py-8">
                <Upload className="h-12 w-12 text-primary" />
                <p className="text-base font-semibold text-foreground">Drop files to attach</p>
                <p className="text-sm text-muted-foreground">Release to add them to your message</p>
              </div>
            </div>
          )}
          <div className="relative flex-1 min-h-0 overflow-hidden">
            {/* Background layer */}
            {(bgImageUrl || chatSettings.backgroundColor) && (
              <div
                className="absolute inset-0 z-0"
                style={{
                  ...msgAreaBgStyle,
                  filter: bgImageUrl && chatSettings.backgroundBlur ? 'blur(8px)' : undefined,
                  transform: bgImageUrl && chatSettings.backgroundBlur ? 'scale(1.05)' : undefined,
                }}
              />
            )}
            <div className="relative z-10 h-full flex flex-col">
              <MessageList
                messages={messages.messages}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                loading={messages.loading}
                loadingMore={messages.loadingMore}
                hasMore={messages.hasMore}
                ref={messageListRef}
                unreadCutoff={unreadCutoff}
                onLoadMore={messages.loadMore}
                onStartEditMessage={handleStartEdit}
                onDeleteMessage={handleDeleteMessage}
                onReplyMessage={handleReply}
                onReactionMessage={handleReaction}
                onOpenDm={handleCreateDM}
              />
            </div>
          </div>
          <TypingIndicator
            typingUsers={typing.typingUsers
              .filter((u) => u.userId !== currentUserId)
              .map((u) => ({ ...u, displayName: memberNameById.get(u.userId) ?? '' }))}
          />
          {activeChannelId && (
            <Suspense
              fallback={
                <div className="border-t border-border p-3 text-xs text-muted-foreground">
                  Loading composer…
                </div>
              }
            >
              <MessageInput
                ref={messageInputRef}
                channelId={activeChannelId}
                onSend={handleSendMessage}
                onSaveEdit={handleSaveEdit}
                onDeleteMessage={handleDeleteMessage}
                onTyping={typing.sendTyping}
                members={members}
                memberCount={memberCount}
                currentUserId={currentUserId}
                disabled={!activeChannelId || !!activeChannel?.hasLeft}
                leftChannel={!!activeChannel?.hasLeft}
                placeholder={
                  activeChannel?.hasLeft
                    ? 'You left this channel'
                    : activeChannel
                      ? `Message ${activeChannel.dmPartnerDisplayName || activeChannel.name || 'channel'}...`
                      : 'Select a channel...'
                }
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
                editingMessage={editingMessage}
                onCancelEdit={() => setEditingMessage(null)}
                permissions={messagePermissions}
              />
            </Suspense>
          )}
        </div>
      </div>

      <ChatSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
      <MentionsPanel
        open={mentionsOpen}
        onOpenChange={setMentionsOpen}
        onSelectMention={handleSelectMention}
      />
      {/* Mounted only while open so each visit starts from an empty query. */}
      {globalSearchOpen && (
        <AllChatsSearchPanel
          open
          onOpenChange={setGlobalSearchOpen}
          initialQuery={globalSearchInitialQuery}
          channels={channels.channels}
          guilds={myGuilds}
          resolveChannelLabel={resolveSearchHitLabel}
          onSelectResult={handleSelectGlobalResult}
          onSelectConversation={handleSelectChannel}
          onSelectPerson={handleSelectPerson}
        />
      )}

      <GuildDirectMessageDialog
        guild={guildDmTarget}
        currentUserId={currentUserId}
        onClose={() => setGuildDmTarget(null)}
        onSelectMember={handleOpenGuildDm}
      />

      {previewChannel && (
        <ChannelMessagePreview
          channel={previewChannel}
          currentUserId={currentUserId}
          onClose={() => setPreviewChannel(null)}
        />
      )}
    </div>
  );
}
